import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Clock3,
  FileText,
  Wallet,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { clearPortalSession } from "../utils/portalAuth";

const navItems = [
  { name: "Dashboard", path: "/admin", icon: LayoutDashboard },
  { name: "Staff", path: "/admin/staff", icon: Users },
  { name: "Attendance", path: "/admin/attendance", icon: Clock3 },
  { name: "Documents", path: "/admin/documents", icon: FileText },
  { name: "Wages", path: "/admin/wages", icon: Wallet },
];

export default function AdminLayout({ children, title = "Dashboard" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    clearPortalSession();
    navigate("/login", { replace: true });
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex">
      {/* MOBILE OVERLAY */}
      {mobileMenuOpen && (
        <button
          type="button"
          aria-label="Close menu overlay"
          onClick={closeMobileMenu}
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px] md:hidden"
        />
      )}

      {/* MOBILE DRAWER */}
      <aside
        className={`fixed left-0 top-0 z-50 flex h-dvh w-72 flex-col border-r border-slate-200 bg-white shadow-xl transition-transform duration-300 md:hidden ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* MOBILE BRAND / CLOSE */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-orange-50 to-amber-50 px-4 py-5">
          <div className="flex items-center gap-3">
            <img
              src="/favicon.webp"
              alt="Lasgidi Restaurant"
              className="h-10 w-10 object-contain"
            />

            <div>
              <h1 className="text-base font-bold text-slate-900">
                Lasgidi Restaurant
              </h1>
              <p className="text-xs font-medium text-orange-600">
                Staff Portal
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={closeMobileMenu}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-600 transition hover:bg-white/70 hover:text-slate-900"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* MOBILE NAV */}
        <nav className="flex-1 space-y-2 p-4">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeMobileMenu}
                className={`group flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-md"
                    : "text-slate-600 hover:bg-gradient-to-r hover:from-slate-100 hover:to-slate-50 hover:text-slate-900"
                }`}
              >
                <Icon
                  size={18}
                  className={`transition ${
                    isActive
                      ? "text-white"
                      : "text-slate-400 group-hover:text-emerald-600"
                  }`}
                />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>

        {/* MOBILE LOGOUT */}
        <div className="border-t border-slate-200 p-4">
          <button
            type="button"
            onClick={() => {
              closeMobileMenu();
              handleLogout();
            }}
            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-slate-600 transition hover:bg-red-50 hover:text-red-600"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* DESKTOP SIDEBAR */}
      <aside className="hidden md:fixed md:left-0 md:top-0 md:flex md:h-dvh md:w-64 md:flex-col md:border-r md:border-slate-200 md:bg-white md:shadow-sm">
        {/* BRAND */}
        <div className="border-b border-slate-200 bg-gradient-to-r from-orange-50 to-amber-50 px-2 py-5">
          <div className="flex items-center gap-3">
            <img
              src="/favicon.webp"
              alt="Lasgidi Restaurant"
              className="h-25 w-10 object-contain"
            />

            <div>
              <h1 className="text-xl font-bold text-slate-900">
                Lasgidi Restaurant
              </h1>
              <p className="text-sm font-medium text-orange-600">
                Staff Portal
              </p>
            </div>
          </div>
        </div>

        {/* NAV */}
        <nav className="flex-1 space-y-2 p-4">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`group flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-md"
                    : "text-slate-600 hover:bg-gradient-to-r hover:from-slate-100 hover:to-slate-50 hover:text-slate-900"
                }`}
              >
                <Icon
                  size={18}
                  className={`transition ${
                    isActive
                      ? "text-white"
                      : "text-slate-400 group-hover:text-emerald-600"
                  }`}
                />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>

        {/* LOGOUT */}
        <div className="border-t border-slate-200 p-4">
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-slate-600 transition hover:bg-red-50 hover:text-red-600"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <main className="min-w-0 flex-1 md:ml-64">
        {/* HEADER */}
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-4 shadow-sm sm:px-6">
          <div className="flex items-center gap-3">
            {/* MOBILE MENU BUTTON */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 md:hidden"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>

            <div>
              <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl">
                {title}
              </h2>
              <p className="text-xs text-slate-500 sm:text-sm">
                Manage staff operations from one place
              </p>
            </div>
          </div>

          {/* AVATAR */}
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-green-600 text-sm font-semibold text-white shadow">
            A
          </div>
        </header>

        <div className="p-4 sm:p-6">{children}</div>
      </main>
    </div>
  );
}